import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';
import aideyLogo from '../assets/sidebar-logo.svg';

// ─── Shared style tokens ──────────────────────────────────────────────────────
const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';
const BORDER = '#35454C';

const inputStyle = {
  display: 'block', width: '100%', padding: '0.7rem 0.9rem',
  border: '1px solid #c5cdd4', borderRadius: 8, fontSize: '0.95rem',
  fontFamily: 'Raleway, sans-serif', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.15s', background: '#fff',
};

const inputDisabledStyle = {
  ...inputStyle,
  background: '#eef1f3', color: '#6a7a7b', cursor: 'not-allowed',
};

const labelStyle = {
  display: 'block', fontSize: '0.82rem', fontWeight: 700,
  color: BORDER, marginBottom: '0.35rem', fontFamily: 'Poppins, sans-serif',
};

const errStyle = {
  background: '#fde8e8', border: '1px solid #f5a5a5', borderRadius: 8,
  padding: '0.65rem 0.9rem', fontSize: '0.85rem', color: '#A12710',
  marginBottom: '1rem', fontFamily: 'Raleway, sans-serif',
};

// ─── Read URL params on load ──────────────────────────────────────────────────
function getUrlParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    email: p.get('email') || '',
    token: p.get('token') || '',
    inviteToken: p.get('inviteToken') || '',
  };
}

// ─── Register / account setup screen ─────────────────────────────────────────
export default function Register() {
  const { register } = useCurrentUser();
  const { t } = useTranslation('common');
  const { email: urlEmail, inviteToken } = getUrlParams();

  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState(urlEmail);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [info, setInfo] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!name.trim()) return setError('Please enter your full name.');
    if (!email.includes('@')) return setError('Please enter a valid email address.');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');

    setLoading(true);
    try {
      const result = await register({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        // 6b: asked here so a new customer's clients never meet "Aidey" in place
        // of their business. Optional — see the field below.
        ...(companyName.trim() && { companyName: companyName.trim() }),
        ...(inviteToken && { inviteToken }),
      });

      // Verify-email-first: no auto-login. Show the "check your inbox" screen — but if the
      // backend reports the verification email did NOT send (Block 173), say so plainly and
      // point at the Resend button rather than a dead-end "check your inbox".
      // Tier 1 #9: from an invitation, the address is already proven — no
      // verification email. Signing in is what joins them to the team.
      if (result && result.readyToSignIn) {
        window.location.href = `/login?email=${encodeURIComponent(email.trim().toLowerCase())}&joining=1`;
        return;
      }
      setDone(true);
      if (result && result.emailSent === false) {
        setInfo(`Your account was created, but we couldn't send the verification email to ${email.trim().toLowerCase()}. Use the button below to resend it.`);
      } else {
        setInfo(`Check your inbox to activate your account. We've sent a verification link to ${email.trim().toLowerCase()}.`);
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await api('/api/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (res && !res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Unable to resend verification email right now.');
      } else {
        setInfo('Verification email sent. Please check your inbox.');
      }
    } catch {
      setError('Unable to resend verification email right now.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
      padding: '1rem', fontFamily: 'Raleway, sans-serif',
    }}>
      <div style={{
        background: '#f5f7f9',
        border: `1px solid ${BORDER}`,
        borderRadius: 16,
        width: '100%',
        maxWidth: 460,
        boxShadow: '0 12px 48px rgba(12,45,72,0.28), 0 2px 8px rgba(12,45,72,0.10)',
      }}>

        {/* Logo header */}
        <div style={{ padding: '2rem 2rem 1.25rem', textAlign: 'center' }}>
          <img
            src={aideyLogo}
            alt="Aidey"
            style={{
              height: 52, width: 'auto', display: 'block', margin: '0 auto',
              border: 'none', outline: 'none', boxShadow: 'none', background: 'transparent',
            }}
          />
        </div>

        <div style={{ borderTop: `1px solid #c5cdd4`, margin: '0 1.5rem' }} />

        <div style={{ padding: '1.75rem 2rem 2rem' }}>
          <h2 style={{
            margin: '0 0 0.35rem', fontSize: '1.15rem', color: NAVY,
            fontFamily: 'Poppins, sans-serif', fontWeight: 700,
          }}>
            {inviteToken ? t('auth.invite_heading') : t('auth.setup_account')}
          </h2>
          <p style={{
            margin: '0 0 1.5rem', fontSize: '0.84rem', color: '#4a5a5b',
            fontFamily: 'Raleway, sans-serif', lineHeight: 1.5,
          }}>
            {inviteToken ? t('auth.invite_desc') : t('auth.welcome_desc')}
          </p>

          {done ? (
            <div style={{
              textAlign: 'center', padding: '2rem 1rem',
              color: '#1e6645', fontFamily: 'Raleway, sans-serif',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
              <p style={{ fontWeight: 700, fontSize: '1rem', margin: 0 }}>{t('auth.account_created')}</p>
              <p style={{ marginTop: '0.6rem', fontSize: '0.88rem', color: '#4a5a5b', lineHeight: 1.5 }}>
                {info || t('auth.verify_prompt')}
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={loading}
                style={{
                  marginTop: '0.9rem',
                  padding: '0.55rem 0.9rem',
                  border: '1px solid #c5cdd4',
                  background: '#fff',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  color: NAVY,
                  fontFamily: 'Raleway, sans-serif',
                }}
              >
                {loading ? t('auth.resending') : t('auth.resend_verification')}
              </button>
              <p style={{ marginTop: '1rem', fontSize: '0.82rem', color: '#4a5a5b' }}>
                <a href="/" style={{ color: NAVY, fontWeight: 700, textDecoration: 'none' }}>{t('auth.back_to_sign_in')}</a>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {error && <div style={errStyle}>⚠ {error}</div>}
              {info && (
                <div style={{
                  background: '#eaf6ef', border: '1px solid #6abf85', borderRadius: 8,
                  padding: '0.65rem 0.9rem', fontSize: '0.85rem', color: '#1e6645',
                  marginBottom: '1rem', fontFamily: 'Raleway, sans-serif',
                }}>
                  {info}
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="register-name" style={labelStyle}>{t('auth.full_name_label')}</label>
                <input
                  id="register-name" name="name"
                  value={name} onChange={e => setName(e.target.value)}
                  placeholder="Jane Smith" style={inputStyle} required
                />
              </div>

              {/* PLAN ITEM 6b: the business name defaulted to EMPTY because nothing
                  ever asked for it, and then the product's name reached the
                  customer's clients. Asked for here, and OPTIONAL: a sole trader
                  working under their own name should not be stopped at the door,
                  and the line says where it shows up so the answer is informed. */}
              {!inviteToken && (
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="register-company" style={labelStyle}>Business name (optional)</label>
                  <input
                    id="register-company" name="companyName"
                    value={companyName} onChange={e => setCompanyName(e.target.value)}
                    placeholder="Smith Consulting"
                    style={inputStyle}
                  />
                  <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem', color: '#8a9a9b', fontFamily: 'Raleway, sans-serif' }}>
                    Your clients see this on invoices, contracts and your booking page. You can add or change it later.
                  </p>
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="register-email" style={labelStyle}>{t('auth.email_label')}</label>
                <input
                  id="register-email" name="email"
                  type="email" value={email}
                  onChange={urlEmail ? undefined : e => setEmail(e.target.value)}
                  readOnly={!!urlEmail}
                  placeholder="you@example.com"
                  style={urlEmail ? inputDisabledStyle : inputStyle}
                  required
                />
                {urlEmail && (
                  <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem', color: '#8a9a9b', fontFamily: 'Raleway, sans-serif' }}>
                    {t('auth.email_for_plan')}
                  </p>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div>
                  <label htmlFor="register-password" style={labelStyle}>{t('auth.password_label')}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="register-password" name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password} onChange={e => setPassword(e.target.value)}
                      placeholder={t('auth.password_hint')}
                      style={{ ...inputStyle, paddingRight: '2.5rem' }} required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute', right: '0.6rem', top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#94a3b8', fontSize: '1.05rem', padding: '0.25rem',
                        lineHeight: 1, display: 'flex', alignItems: 'center',
                      }}
                    >
                      {showPassword ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="register-confirm-password" style={labelStyle}>{t('auth.confirm_password')}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="register-confirm-password" name="confirmPassword"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm} onChange={e => setConfirm(e.target.value)}
                      placeholder={t('auth.repeat_password')}
                      style={{ ...inputStyle, paddingRight: '2.5rem' }} required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(v => !v)}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute', right: '0.6rem', top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#94a3b8', fontSize: '1.05rem', padding: '0.25rem',
                        lineHeight: 1, display: 'flex', alignItems: 'center',
                      }}
                    >
                      {showConfirm ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '0.75rem',
                background: loading ? '#c8cabb' : YELLOW,
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '1rem',
                fontFamily: 'Poppins, sans-serif', cursor: loading ? 'not-allowed' : 'pointer',
                color: '#242629', transition: 'background 0.2s',
              }}>
                {loading ? t('auth.creating_account') : t('auth.create_account')}
              </button>

              <p style={{
                textAlign: 'center', marginTop: '1.1rem', fontSize: '0.82rem',
                color: '#4a5a5b', fontFamily: 'Raleway, sans-serif',
              }}>
                {t('auth.have_account')}{' '}
                <a href="/" style={{ color: NAVY, fontWeight: 700, textDecoration: 'none' }}>{t('auth.sign_in')}</a>
              </p>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
